import React, { useEffect, useState } from 'react';
import { 
  ShoppingBag, 
  Filter, 
  Download, 
  RefreshCw,
  Search,
  ChevronDown,
  DollarSign,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  Package,
  Truck,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight
} from 'lucide-react';
import OrderTable from '../../components/admin/marketplace/OrderTable';
import useAdminStore from '../../store/adminStore';
import { toast } from 'react-hot-toast';

const AdminOrdersPage = () => {
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    status: '',
    paymentStatus: '',
    minAmount: '',
    maxAmount: '',
    search: '',
  });

  const [showFilters, setShowFilters] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    completed: 0,
    cancelled: 0,
    revenue: 0
  });

  const {
    marketplaceOrders,
    marketplaceOrdersPagination,
    isLoading,
    getAllOrders,
    getOrderDetailsAdmin,
  } = useAdminStore();

  useEffect(() => {
    loadOrders();
  }, [filters.page, filters.limit, filters.status, filters.paymentStatus]);

  useEffect(() => {
    calculateStats();
  }, [marketplaceOrders]);

  const loadOrders = async () => {
    try {
      await getAllOrders(filters);
    } catch (error) {
      console.error('Failed to load orders:', error);
    }
  };

  const calculateStats = () => {
    const orders = marketplaceOrders || [];
    const total = orders.length;
    const pending = orders.filter(o => o.status === 'PENDING').length;
    const completed = orders.filter(o => ['DELIVERED', 'COMPLETED'].includes(o.status)).length;
    const cancelled = orders.filter(o => o.status === 'CANCELLED').length;
    const revenue = orders
      .filter(o => o.status !== 'CANCELLED')
      .reduce((sum, o) => sum + (o.totalAmount || 0), 0);

    setStats({
      total,
      pending,
      completed,
      cancelled,
      revenue
    });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setFilters({ ...filters, page: 1 });
    loadOrders();
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value, page: 1 });
  };

  const handleRefresh = () => {
    loadOrders();
    toast.success('Orders refreshed');
  };

  const handleExport = () => {
    toast.success('Export started');
  };

  const handleViewOrder = async (order) => {
    try {
      const details = await getOrderDetailsAdmin(order.id);
      setSelectedOrder(details);
      toast.success('Order details loaded');
    } catch (error) {
      console.error('Failed to load order details:', error);
    }
  };

  const handleEditOrder = (order) => {
    setSelectedOrder(order);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= (marketplaceOrdersPagination?.totalPages || 1)) {
      setFilters({ ...filters, page: newPage });
    }
  };

  const handleItemsPerPageChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setItemsPerPage(newLimit);
    setFilters({ ...filters, limit: newLimit, page: 1 });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num || 0);
  };

  const total = marketplaceOrdersPagination?.total || 0;
  const currentPage = marketplaceOrdersPagination?.page || 1;
  const totalPages = marketplaceOrdersPagination?.totalPages || 1;
  const limit = marketplaceOrdersPagination?.limit || 20;
  const startIndex = (currentPage - 1) * limit + 1;
  const endIndex = Math.min(currentPage * limit, total);

  return (
    <div className="min-h-screen bg-[#E8ECF5] p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-[#E8ECF5] rounded-xl p-3 shadow-neomorph mb-3 border border-primary-100/50">
          <div className="flex flex-col md:flex-row md:items-center justify-between">
            <div>
              <div className="flex items-center space-x-1.5 mb-0.5">
                <div className="w-1 h-5 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full" />
                <h1 className="text-lg font-bold text-gray-800">Order Management</h1>
              </div>
              <p className="text-xs text-gray-500 ml-2.5">Monitor and manage all orders</p>
            </div>
            
            <div className="mt-2 md:mt-0 flex items-center space-x-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="bg-white rounded-lg px-3 py-1.5 shadow-neomorph hover:shadow-neomorph-hover transition-all text-xs font-medium text-gray-700 flex items-center border border-gray-100"
              >
                <Filter className="h-3.5 w-3.5 mr-1.5" />
                Filters
                <ChevronDown className={`h-3.5 w-3.5 ml-1.5 transform transition-transform ${
                  showFilters ? 'rotate-180' : ''
                }`} />
              </button>
              <button
                onClick={handleExport}
                className="bg-white rounded-lg px-3 py-1.5 shadow-neomorph hover:shadow-neomorph-hover transition-all text-xs font-medium text-gray-700 flex items-center border border-gray-100"
              >
                <Download className="h-3.5 w-3.5 mr-1.5" />
                Export
              </button>
              <button
                onClick={handleRefresh}
                disabled={isLoading}
                className="bg-white rounded-lg px-3 py-1.5 shadow-neomorph hover:shadow-neomorph-hover transition-all text-xs font-medium text-gray-700 flex items-center border border-gray-100"
              >
                <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
          <div className="bg-white rounded-lg p-3 shadow-neomorph border border-gray-100">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] text-gray-600 mb-0.5">Total Orders</p>
                <p className="text-base font-bold text-gray-800">{formatNumber(stats.total)}</p>
              </div>
              <div className="p-1.5 bg-gradient-to-br from-primary-500 to-primary-600 rounded-md shadow-md">
                <ShoppingBag className="h-3.5 w-3.5 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-3 shadow-neomorph border border-gray-100">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] text-gray-600 mb-0.5">Pending</p>
                <p className="text-base font-bold text-yellow-600">{formatNumber(stats.pending)}</p>
              </div>
              <div className="p-1.5 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-md shadow-md">
                <Clock className="h-3.5 w-3.5 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-3 shadow-neomorph border border-gray-100">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] text-gray-600 mb-0.5">Completed</p>
                <p className="text-base font-bold text-green-600">{formatNumber(stats.completed)}</p>
              </div>
              <div className="p-1.5 bg-gradient-to-br from-green-500 to-green-600 rounded-md shadow-md">
                <CheckCircle className="h-3.5 w-3.5 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-3 shadow-neomorph border border-gray-100">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] text-gray-600 mb-0.5">Cancelled</p>
                <p className="text-base font-bold text-red-600">{formatNumber(stats.cancelled)}</p>
              </div>
              <div className="p-1.5 bg-gradient-to-br from-red-500 to-red-600 rounded-md shadow-md">
                <XCircle className="h-3.5 w-3.5 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-3 shadow-neomorph border border-gray-100">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] text-gray-600 mb-0.5">Revenue</p>
                <p className="text-base font-bold text-purple-600">{formatCurrency(stats.revenue)}</p>
              </div>
              <div className="p-1.5 bg-gradient-to-br from-purple-500 to-purple-600 rounded-md shadow-md">
                <DollarSign className="h-3.5 w-3.5 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Status Distribution */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          <div className="bg-white rounded-lg p-3 shadow-neomorph border border-gray-100">
            <div className="flex items-center space-x-2.5">
              <div className="p-1.5 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-md shadow-md">
                <Package className="h-3.5 w-3.5 text-white" />
              </div>
              <div>
                <p className="text-[10px] text-gray-500">Processing</p>
                <p className="text-xs font-semibold text-gray-800">
                  {formatNumber(marketplaceOrders?.filter(o => 
                    ['CONFIRMED', 'PROCESSING'].includes(o.status)
                  ).length || 0)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-3 shadow-neomorph border border-gray-100">
            <div className="flex items-center space-x-2.5">
              <div className="p-1.5 bg-gradient-to-br from-blue-500 to-blue-600 rounded-md shadow-md">
                <Truck className="h-3.5 w-3.5 text-white" />
              </div>
              <div>
                <p className="text-[10px] text-gray-500">Shipped</p>
                <p className="text-xs font-semibold text-gray-800">
                  {formatNumber(marketplaceOrders?.filter(o => o.status === 'SHIPPED').length || 0)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-3 shadow-neomorph border border-gray-100">
            <div className="flex items-center space-x-2.5">
              <div className="p-1.5 bg-gradient-to-br from-green-500 to-green-600 rounded-md shadow-md">
                <CheckCircle className="h-3.5 w-3.5 text-white" />
              </div>
              <div>
                <p className="text-[10px] text-gray-500">Delivered</p>
                <p className="text-xs font-semibold text-gray-800">
                  {formatNumber(marketplaceOrders?.filter(o => o.status === 'DELIVERED').length || 0)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-3 shadow-neomorph border border-gray-100">
            <div className="flex items-center space-x-2.5">
              <div className="p-1.5 bg-gradient-to-br from-purple-500 to-purple-600 rounded-md shadow-md">
                <TrendingUp className="h-3.5 w-3.5 text-white" />
              </div>
              <div>
                <p className="text-[10px] text-gray-500">Avg Order Value</p>
                <p className="text-xs font-semibold text-gray-800">
                  {formatCurrency(stats.completed > 0 ? stats.revenue / stats.completed : 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="bg-white rounded-xl p-3 shadow-neomorph border border-gray-100 mb-3">
            <form onSubmit={handleSearch} className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div>
                  <label className="block text-[10px] font-medium text-gray-600 mb-1">Search</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={filters.search}
                      onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                      placeholder="Order #, Reference..."
                      className="block w-full pl-8 pr-2 py-1.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                    />
                    <Search className="absolute left-2.5 top-1.5 h-3.5 w-3.5 text-gray-400" />
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-medium text-gray-600 mb-1">Order Status</label>
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="block w-full px-2 py-1.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                  >
                    <option value="">All</option>
                    <option value="PENDING">Pending</option>
                    <option value="CONFIRMED">Confirmed</option>
                    <option value="PROCESSING">Processing</option>
                    <option value="SHIPPED">Shipped</option>
                    <option value="DELIVERED">Delivered</option>
                    <option value="COMPLETED">Completed</option>
                    <option value="CANCELLED">Cancelled</option>
                    <option value="REFUNDED">Refunded</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[10px] font-medium text-gray-600 mb-1">Payment Status</label>
                  <select
                    value={filters.paymentStatus}
                    onChange={(e) => handleFilterChange('paymentStatus', e.target.value)}
                    className="block w-full px-2 py-1.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                  >
                    <option value="">All</option>
                    <option value="PAID">Paid</option>
                    <option value="PENDING">Pending</option>
                    <option value="FAILED">Failed</option>
                    <option value="REFUNDED">Refunded</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[10px] font-medium text-gray-600 mb-1">Amount Range</label>
                  <div className="flex space-x-1.5">
                    <input
                      type="number"
                      value={filters.minAmount}
                      onChange={(e) => handleFilterChange('minAmount', e.target.value)}
                      placeholder="Min"
                      className="block w-full px-2 py-1.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                    />
                    <input
                      type="number"
                      value={filters.maxAmount}
                      onChange={(e) => handleFilterChange('maxAmount', e.target.value)}
                      placeholder="Max"
                      className="block w-full px-2 py-1.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setFilters({
                      page: 1,
                      limit: 20,
                      status: '',
                      paymentStatus: '',
                      minAmount: '',
                      maxAmount: '',
                      search: '',
                    });
                  }}
                  className="bg-white rounded-lg px-3 py-1.5 shadow-neomorph hover:shadow-neomorph-hover transition-all text-xs font-medium text-gray-700 border border-gray-100"
                >
                  Clear
                </button>
                <button
                  type="submit"
                  className="bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg px-3 py-1.5 shadow-md shadow-primary-500/30 hover:shadow-lg hover:shadow-primary-500/40 transition-all text-xs font-medium"
                >
                  Apply
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Orders Table */}
        <div className="bg-white rounded-xl p-3 shadow-neomorph border border-gray-100 mb-3">
          <OrderTable
            orders={marketplaceOrders || []}
            pagination={marketplaceOrdersPagination}  // ← ADD THIS
            onView={handleViewOrder}
            onEdit={handleEditOrder}
            onRefresh={loadOrders}
            onPageChange={handlePageChange}          // ← ADD THIS
            isLoading={isLoading}
          />
        </div>

        {/* Pagination Controls */}
        {marketplaceOrdersPagination && marketplaceOrdersPagination.total > 0 && (
          <div className="flex flex-col md:flex-row items-center justify-between gap-3">
            <div className="flex items-center space-x-2">
              <label className="text-xs text-gray-600">Show:</label>
              <select
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
                className="px-1.5 py-1 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-xs text-gray-500">per page</span>
            </div>

            <p className="text-xs text-gray-500 bg-white rounded-lg px-2.5 py-1 shadow-neomorph-inset">
              Showing <span className="font-medium text-gray-800">{startIndex}</span> to{' '}
              <span className="font-medium text-gray-800">{endIndex}</span> of{' '}
              <span className="font-medium text-gray-800">{total}</span> orders
            </p>

            <div className="flex items-center space-x-1">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1 || isLoading}
                className={`p-1.5 rounded-lg transition-all ${
                  currentPage === 1 || isLoading
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                }`}
                title="First"
              >
                <ChevronsLeft className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || isLoading}
                className={`p-1.5 rounded-lg transition-all ${
                  currentPage === 1 || isLoading
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                }`}
                title="Previous"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>

              <div className="flex items-center space-x-0.5">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`w-7 h-7 rounded-lg text-xs font-medium transition-all ${
                        currentPage === pageNum
                          ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md shadow-primary-500/30'
                          : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || isLoading}
                className={`p-1.5 rounded-lg transition-all ${
                  currentPage === totalPages || isLoading
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                }`}
                title="Next"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages || isLoading}
                className={`p-1.5 rounded-lg transition-all ${
                  currentPage === totalPages || isLoading
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                }`}
                title="Last"
              >
                <ChevronsRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        .shadow-neomorph {
          box-shadow: 6px 6px 11px #d0d6e0, -6px -6px 11px #ffffff;
        }
        .shadow-neomorph-hover {
          box-shadow: 4px 4px 7px #d0d6e0, -4px -4px 7px #ffffff;
        }
        .shadow-neomorph-inset {
          box-shadow: inset 2.5px 2.5px 5px #d0d6e0, inset -2.5px -2.5px 5px #ffffff;
        }
      `}</style>
    </div>
  );
};

export default AdminOrdersPage;